import "server-only";

import { cache } from "react";

import { getAdminDb } from "../firebase/admin";
import { PageSchema, type Page } from "../schema/pages";
import { SymbolSchema, type StoredSymbol } from "../schema/symbols";
import { CustomBlockSchema, type CustomBlock } from "../schema/customBlocks";
import { PublicSiteSettingsSchema, type PublicSiteSettings } from "../schema/settings";
import { localizePage, localizeBlocks } from "../i18n/overlay";

export type StoredPage = Page & { id: string };

function toPath(slugs: string[] | undefined): string {
  const clean = (slugs ?? []).filter(Boolean);
  return "/" + clean.join("/");
}

/** Pad uit Next catch-all params (`[[...slug]]`). "/" voor de root. */
export function pathFromSlugs(slugs: string[] | undefined): string {
  const p = toPath(slugs);
  return p === "/" ? "/" : p.replace(/\/$/, "");
}

function parse(id: string, data: unknown): StoredPage | null {
  const res = PageSchema.safeParse(data);
  if (!res.success) {
    // Niet stil 404'en: een gepubliceerde pagina die het schema niet haalt is een diagnose-signaal
    // (schema-evolutie zonder default, of corrupte write) — zelfde patroon als symbols/customBlocks.
    console.warn(`[xicms] pagina "${id}" faalt schema-validatie en wordt overgeslagen:`, res.error.issues[0]);
    return null;
  }
  return { id, ...res.data };
}

/**
 * Admin-SDK-reads mogen een PUBLIEKE pagina nooit laten 500'en — zelfde belofte als het REST-pad
 * (dat al overal try/catch → lege fallback heeft). Een Firestore-hikje wordt gelogd en levert de
 * fallback op; de route rendert dan 404/fallback i.p.v. een servererror.
 */
async function safeAdminRead<T>(fallback: T, what: string, fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (e) {
    console.warn(`[xicms] Admin-read mislukt (${what}) — fallback gebruikt:`, (e as Error)?.message ?? e);
    return fallback;
  }
}

/* ---------------- keyless public reads via Firestore REST (alleen apiKey) ----------------
 * Gepubliceerde pagina's zijn publiek leesbaar (rules). De Firestore REST-API honoreert die
 * rules met enkel de web-apiKey (geen Admin-credentials, geen client-SDK), dus dit werkt
 * server-side op keyless deployments (org-policy zonder service-account-keys).
 */
type FsValue = Record<string, unknown>;
function decode(v: FsValue): unknown {
  if (v == null) return null;
  if ("stringValue" in v) return v.stringValue;
  if ("integerValue" in v) return Number(v.integerValue);
  if ("doubleValue" in v) return v.doubleValue;
  if ("booleanValue" in v) return v.booleanValue;
  if ("nullValue" in v) return null;
  if ("timestampValue" in v) return v.timestampValue;
  if ("referenceValue" in v) return v.referenceValue;
  if ("geoPointValue" in v) return v.geoPointValue;
  if ("arrayValue" in v) return (((v.arrayValue as FsValue)?.values as FsValue[]) ?? []).map(decode);
  if ("mapValue" in v) return decodeFields(((v.mapValue as FsValue)?.fields as Record<string, FsValue>) ?? {});
  return null;
}
function decodeFields(fields: Record<string, FsValue>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(fields).map(([k, x]) => [k, decode(x)]));
}

interface Filter { fieldFilter: { field: { fieldPath: string }; op: "EQUAL"; value: { stringValue: string } } }
function eq(fieldPath: string, value: string): Filter {
  return { fieldFilter: { field: { fieldPath }, op: "EQUAL", value: { stringValue: value } } };
}

async function restQuery(siteId: string, filters: Filter[], limit?: number): Promise<StoredPage[]> {
  const apiKey = process.env.NEXT_PUBLIC_FIREBASE_API_KEY;
  const projectId = process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID;
  if (!apiKey || !projectId) return [];
  const url = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/sites/${siteId}:runQuery?key=${apiKey}`;
  const where = filters.length === 1 ? filters[0] : { compositeFilter: { op: "AND", filters } };
  const structuredQuery: Record<string, unknown> = { from: [{ collectionId: "pages" }], where };
  if (limit) structuredQuery.limit = limit;
  // Netwerk-/parsefouten mogen een publieke pagina nooit laten crashen (500) — dan liever een
  // lege lijst (Next toont dan een 404 of de fallback, en de volgende revalidate probeert opnieuw).
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ structuredQuery }),
      // Pagina's zijn ISR-gecached; next-cache mag dit hergebruiken.
      next: { revalidate: 300 },
    });
    if (!res.ok) return [];
    const rows = (await res.json()) as { document?: { name: string; fields: Record<string, FsValue> } }[];
    return rows
      .filter((r) => r.document)
      .map((r) => parse(r.document!.name.split("/").pop()!, decodeFields(r.document!.fields)))
      .filter((p): p is StoredPage => p !== null);
  } catch (err) {
    console.error("[xicms] Firestore REST-query mislukt:", err);
    return [];
  }
}

/** Alle gepubliceerde pagina's (sitemap / generateStaticParams). Keyless via REST als geen Admin. */
export async function listPublishedPages(siteId: string): Promise<StoredPage[]> {
  const adb = getAdminDb();
  if (adb) {
    return safeAdminRead([], `listPublishedPages ${siteId}`, async () => {
      const snap = await adb.collection(`sites/${siteId}/pages`).where("status", "==", "published").get();
      return snap.docs.map((d) => parse(d.id, d.data())).filter((p): p is StoredPage => p !== null);
    });
  }
  return restQuery(siteId, [eq("status", "published")]);
}

/**
 * Per-request-dedup via React cache(): metadata (generateMetadata) én de render lezen dezelfde
 * pagina — zonder cache() waren dat twee identieke Firestore-reads per request. NB: de
 * `next:{revalidate}`-hint op het REST-pad dekt dit niet (POST-fetches cachen niet); route-level
 * ISR (revalidate=300 in de consumer) blijft de echte kosten-demper tussen requests.
 */
export const getPublishedPageByPath = cache(getPublishedPageByPathUncached);
export const getSiteSymbols = cache(getSiteSymbolsUncached);
export const getSiteCustomBlocks = cache(getSiteCustomBlocksUncached);
export const getPublicSiteSettings = cache(getPublicSiteSettingsUncached);

/** Eén gepubliceerde pagina op pad. Keyless via REST als geen Admin. */
async function getPublishedPageByPathUncached(siteId: string, path: string): Promise<StoredPage | null> {
  const adb = getAdminDb();
  if (adb) {
    return safeAdminRead(null, `getPublishedPageByPath ${siteId}${path}`, async () => {
      const snap = await adb
        .collection(`sites/${siteId}/pages`)
        .where("status", "==", "published")
        .where("path", "==", path)
        .limit(1)
        .get();
      const d = snap.docs[0];
      return d ? parse(d.id, d.data()) : null;
    });
  }
  const rows = await restQuery(siteId, [eq("status", "published"), eq("path", path)], 1);
  return rows[0] ?? null;
}

/** Draft-versie (voor preview achter auth). */
export async function getDraftPageById(siteId: string, pageId: string): Promise<StoredPage | null> {
  const db = getAdminDb();
  if (!db) return null;
  return safeAdminRead(null, `getDraftPageById ${siteId}/${pageId}`, async () => {
    const d = await db.doc(`sites/${siteId}/drafts/${pageId}`).get();
    return d.exists ? parse(d.id, d.data()) : null;
  });
}

/** Draft op pad (preview). */
export async function getDraftPageByPath(siteId: string, path: string): Promise<StoredPage | null> {
  const db = getAdminDb();
  if (!db) return null;
  return safeAdminRead(null, `getDraftPageByPath ${siteId}${path}`, async () => {
    const snap = await db.collection(`sites/${siteId}/drafts`).where("path", "==", path).limit(1).get();
    const d = snap.docs[0];
    return d ? parse(d.id, d.data()) : null;
  });
}

/* ---------------- symbols & custom blocks (5.0) ---------------- */

/** Generieke publieke collectie-read onder sites/{siteId}/ — Admin als beschikbaar, anders REST. */
async function readCollection(siteId: string, collectionId: string): Promise<{ id: string; data: Record<string, unknown> }[]> {
  const adb = getAdminDb();
  if (adb) {
    return safeAdminRead([], `readCollection ${siteId}/${collectionId}`, async () => {
      const snap = await adb.collection(`sites/${siteId}/${collectionId}`).get();
      return snap.docs.map((d) => ({ id: d.id, data: d.data() as Record<string, unknown> }));
    });
  }
  const apiKey = process.env.NEXT_PUBLIC_FIREBASE_API_KEY;
  const projectId = process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID;
  if (!apiKey || !projectId) return [];
  const url = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/sites/${siteId}:runQuery?key=${apiKey}`;
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ structuredQuery: { from: [{ collectionId }] } }),
      next: { revalidate: 300 },
    });
    if (!res.ok) return [];
    const rows = (await res.json()) as { document?: { name: string; fields: Record<string, FsValue> } }[];
    return rows
      .filter((r) => r.document)
      .map((r) => ({ id: r.document!.name.split("/").pop()!, data: decodeFields(r.document!.fields ?? {}) }));
  } catch (err) {
    console.error(`[xicms] Firestore REST-read van ${collectionId} mislukt:`, err);
    return [];
  }
}

/** Alle symbolen van een site (gevalideerd; ongeldige overgeslagen met waarschuwing). */
async function getSiteSymbolsUncached(siteId: string): Promise<StoredSymbol[]> {
  const rows = await readCollection(siteId, "symbols");
  const out: StoredSymbol[] = [];
  for (const r of rows) {
    const res = SymbolSchema.safeParse(r.data);
    if (res.success) out.push({ id: r.id, ...res.data });
    else console.warn(`[xicms] symbool "${r.id}" ongeldig — overgeslagen.`);
  }
  return out;
}

/**
 * Vervangt symbolRef-blokken door de blokken van hun symbool (één niveau — symbolen kunnen zelf
 * geen symbolRef bevatten). `_key`s van de ingevoegde blokken worden geprefixt met de ref-key
 * zodat ze uniek blijven wanneer hetzelfde symbool vaker op een pagina staat. Verweesde refs
 * verdwijnen stil (de pagina mag nooit breken op een verwijderd symbool).
 */
export function expandSymbols<T extends { blocks?: unknown[] }>(page: T, symbols: StoredSymbol[]): T {
  const byId = new Map(symbols.map((s) => [s.id, s]));
  const blocks = (page.blocks ?? []).flatMap((b) => {
    const blk = b as { _type?: string; _key?: string; symbolId?: string };
    if (blk?._type !== "symbolRef") return [b];
    const sym = blk.symbolId ? byId.get(blk.symbolId) : undefined;
    if (!sym) return [];
    return sym.blocks.map((sb, i) => ({ ...(sb as Record<string, unknown>), _key: `${blk._key ?? "sym"}-${(sb as { _key?: string })._key ?? i}` }));
  });
  return { ...page, blocks };
}

/**
 * Gepubliceerde pagina op pad mét symbolRef-expansie (SEO: symboolinhoud zit in de SSR-HTML).
 * Geef `locale` mee (een NIET-default taal) om de vertaal-overlay toe te passen — op de pagina
 * én op de ingevoegde symbolen (elk symbool draagt zijn eigen `translations`).
 */
export async function getPublishedPageByPathExpanded(
  siteId: string,
  path: string,
  locale?: string,
): Promise<StoredPage | null> {
  let page = await getPublishedPageByPath(siteId, path);
  if (!page) return null;
  // De caller geeft alléén een niet-default taal door; "" als default matcht dus nooit.
  if (locale) page = localizePage(page, locale, "");
  if (!(page.blocks ?? []).some((b) => (b as { _type?: string })._type === "symbolRef")) return page;
  let symbols = await getSiteSymbols(siteId);
  if (locale) {
    symbols = symbols.map((s) => ({
      ...s,
      blocks: localizeBlocks(s.blocks as Array<{ _key?: string }>, s.translations?.[locale]) as typeof s.blocks,
    }));
  }
  return expandSymbols(page, symbols);
}

/**
 * Publieke site-instellingen (`sites/{siteId}/settings/public`) server-side — voor de
 * `settingsData`-prop van XiCMSProvider. Zo zit het klant-thema/nav/footer al in de server-HTML
 * (geen theme-flits bij eerste paint) én vervalt de per-bezoeker `getDoc` in de browser: de
 * layout-route is ISR-gecached, dus dit kost ~1 read per revalidate i.p.v. per bezoeker.
 * Fouten leveren `{}` op — de site valt dan terug op de statische SiteConfig.
 */
async function getPublicSiteSettingsUncached(siteId: string): Promise<PublicSiteSettings> {
  const adb = getAdminDb();
  let data: unknown = {};
  if (adb) {
    data = await safeAdminRead<unknown>({}, `getPublicSiteSettings ${siteId}`, async () => {
      const d = await adb.doc(`sites/${siteId}/settings/public`).get();
      return d.exists ? d.data() : {};
    });
  } else {
    const apiKey = process.env.NEXT_PUBLIC_FIREBASE_API_KEY;
    const projectId = process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID;
    if (apiKey && projectId) {
      const url = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/sites/${siteId}/settings/public?key=${apiKey}`;
      try {
        // GET-fetches worden — anders dan de runQuery-POSTs — wél door de Next-datacache gedekt.
        const res = await fetch(url, { next: { revalidate: 300 } });
        if (res.ok) {
          const doc = (await res.json()) as { fields?: Record<string, FsValue> };
          data = decodeFields(doc.fields ?? {});
        }
      } catch (err) {
        console.error("[xicms] Firestore REST-read van settings/public mislukt:", err);
      }
    }
  }
  const parsed = PublicSiteSettingsSchema.safeParse(data ?? {});
  return parsed.success ? parsed.data : {};
}

/** Alle custom blocks van een site (gevalideerd) — voor SSR-registratie via XiCMSProvider. */
async function getSiteCustomBlocksUncached(siteId: string): Promise<CustomBlock[]> {
  const rows = await readCollection(siteId, "customBlocks");
  const out: CustomBlock[] = [];
  for (const r of rows) {
    const res = CustomBlockSchema.safeParse({ ...r.data, type: r.data.type ?? r.id });
    if (res.success) out.push(res.data);
    else console.warn(`[xicms] custom block "${r.id}" ongeldig — overgeslagen.`);
  }
  return out;
}
